import { API_ENDPOINTS } from "@/config/api";
import { fetchApi } from "./api";

export type CapabilityScopeType = "global" | "source" | "user";
export type CapabilityOverrideState = "allow" | "deny";
export type CapabilityKind = "entitlement" | "ops" | "rollout";

export interface CapabilityOverride {
  id: number;
  capability: string;
  scope_type: CapabilityScopeType;
  scope_id: string;
  state: CapabilityOverrideState;
  reason: string;
  expires_at: string | null;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

/** Per-spec metadata for one capability, as declared in the Python registry. */
export interface CapabilityRegistryEntry {
  capability: string;
  description: string;
  kind: CapabilityKind;
  source_scopable: boolean;
  sold_manually: boolean;
  admin_bypass: boolean;
  requires: string | null;
  required_tier: string | null;
}

export interface CapabilityOverrideTemplateItem {
  capability: string;
  state: CapabilityOverrideState;
}

export interface CapabilityOverrideTemplate {
  id: string;
  name: string;
  description: string;
  suggested_scope_type: CapabilityScopeType;
  overrides: CapabilityOverrideTemplateItem[];
}

export interface CapabilityOverrideBulkItem {
  capability: string;
  state: CapabilityOverrideState;
}

export interface CapabilityOverrideBulkCreate {
  scope_type: CapabilityScopeType;
  scope_id: string;
  reason: string;
  expires_at?: string | null;
  overrides: CapabilityOverrideBulkItem[];
}

export interface CapabilityOverrideBulkWriteResult {
  results: CapabilityOverrideWriteResult[];
}

export interface CapabilityExplainRequires {
  capability: string;
  allowed: boolean;
  layer: string;
}

export interface CapabilityExplain {
  capability: string;
  allowed: boolean;
  layer: string;
  required_tier: string | null;
  kind: CapabilityKind;
  admin_bypass: boolean;
  requires: CapabilityExplainRequires | null;
}

export interface CapabilityOverrideWriteResult {
  override: CapabilityOverride | null;
  changed: boolean;
}

export interface CreateCapabilityOverrideRequest {
  capability: string;
  scope_type: CapabilityScopeType;
  scope_id: string;
  state: CapabilityOverrideState;
  reason: string;
  expires_at?: string | null;
}

export async function listCapabilityOverrides(
  capability?: string,
): Promise<CapabilityOverride[]> {
  const url = new URL(API_ENDPOINTS.adminCapabilities);
  if (capability) url.searchParams.set("capability", capability);
  return fetchApi<CapabilityOverride[]>(url.toString());
}

export async function createCapabilityOverride(
  body: CreateCapabilityOverrideRequest,
): Promise<CapabilityOverrideWriteResult> {
  return fetchApi<CapabilityOverrideWriteResult>(
    API_ENDPOINTS.adminCapabilities,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

export async function bulkCreateCapabilityOverrides(
  body: CapabilityOverrideBulkCreate,
): Promise<CapabilityOverrideBulkWriteResult> {
  return fetchApi<CapabilityOverrideBulkWriteResult>(
    API_ENDPOINTS.adminCapabilitiesBulk,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

export async function deleteCapabilityOverride(
  overrideId: number,
): Promise<void> {
  await fetchApi<void>(API_ENDPOINTS.adminCapability(overrideId), {
    method: "DELETE",
  });
}

export async function fetchCapabilityRegistry(): Promise<
  CapabilityRegistryEntry[]
> {
  return fetchApi<CapabilityRegistryEntry[]>(
    API_ENDPOINTS.adminCapabilityRegistry,
  );
}

export async function listCapabilityTemplates(): Promise<
  CapabilityOverrideTemplate[]
> {
  return fetchApi<CapabilityOverrideTemplate[]>(
    API_ENDPOINTS.adminCapabilityTemplates,
  );
}

export async function fetchCapabilityExplain(
  capability: string,
  params: { userId?: number | undefined; sourceId?: string | undefined } = {},
): Promise<CapabilityExplain> {
  const url = new URL(API_ENDPOINTS.adminCapabilityExplain(capability));
  if (params.userId != null)
    url.searchParams.set("user_id", String(params.userId));
  if (params.sourceId) url.searchParams.set("source_id", params.sourceId);
  return fetchApi<CapabilityExplain>(url.toString());
}

export async function listOrphanCapabilityOverrides(): Promise<
  CapabilityOverride[]
> {
  return fetchApi<CapabilityOverride[]>(API_ENDPOINTS.adminCapabilityOrphans);
}

export async function listExpiredCapabilityOverrides(): Promise<
  CapabilityOverride[]
> {
  return fetchApi<CapabilityOverride[]>(API_ENDPOINTS.adminCapabilityExpired);
}

export async function purgeExpiredCapabilityOverrides(): Promise<{
  purged: number;
}> {
  return fetchApi<{ purged: number }>(
    API_ENDPOINTS.adminCapabilityPurgeExpired,
    { method: "POST" },
  );
}
